import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Stack,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  GroupAdd as GroupAddIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

const SupervisorAssignments = () => {
  const { isAdmin } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignmentMode, setAssignmentMode] = useState('single'); // 'single' or 'bulk'
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);

  // Fetch assignments and available users
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch current assignments
      const assignmentsResponse = await api.get('/api/supervisor/assignments');
      if (assignmentsResponse.data.success) {
        setAssignments(assignmentsResponse.data.assignments);
      }

      // Fetch available users and supervisors
      const usersResponse = await api.get('/api/supervisor/available-users');
      if (usersResponse.data.success) {
        setSupervisors(usersResponse.data.supervisors);
        setAvailableUsers(usersResponse.data.available_users);
      }
    } catch (error) {
      logger.error('Error fetching supervisor data:', error);
      showSnackbar('Failed to load supervisor assignments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateAssignment = async () => {
    if (!selectedSupervisor) {
      showSnackbar('Please select a supervisor', 'warning');
      return;
    }

    if (assignmentMode === 'single' && !selectedUser) {
      showSnackbar('Please select a user', 'warning');
      return;
    }

    if (assignmentMode === 'bulk' && selectedUsers.length === 0) {
      showSnackbar('Please select at least one user', 'warning');
      return;
    }

    try {
      if (assignmentMode === 'bulk') {
        // Use bulk assignment
        const response = await api.post('/api/supervisor/assignments/bulk', {
          supervisor_id: selectedSupervisor,
          assigned_user_ids: selectedUsers
        });

        if (response.data.success) {
          const summary = response.data.summary;
          let message = '';
          
          if (summary) {
            const parts = [];
            if (summary.created > 0) parts.push(`${summary.created} assigned`);
            if (summary.skipped > 0) parts.push(`${summary.skipped} skipped`);
            if (summary.errors > 0) parts.push(`${summary.errors} errors`);
            message = `Bulk assignment completed: ${parts.join(', ')}`;
          } else {
            const successCount = response.data.assignments?.length || selectedUsers.length;
            message = `Successfully assigned ${successCount} user(s) to supervisor`;
          }
          
          showSnackbar(message, 'success');
          handleCloseDialog();
          fetchData(); // Refresh the data
        } else {
          showSnackbar(response.data.message || 'Failed to create assignments', 'error');
        }
      } else {
        // Single assignment
        const response = await api.post('/api/supervisor/assignments', {
          supervisor_id: selectedSupervisor,
          assigned_user_id: selectedUser
        });

        if (response.data.success) {
          showSnackbar(response.data.message || 'Assignment created successfully', 'success');
          handleCloseDialog();
          fetchData(); // Refresh the data
        } else {
          showSnackbar(response.data.message || 'Failed to create assignment', 'error');
        }
      }
    } catch (error) {
      logger.error('Error creating assignment:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to create assignment(s)', 
        'error'
      );
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSupervisor('');
    setSelectedUser('');
    setSelectedUsers([]);
    setAssignmentMode('single');
  };

  const handleDeleteAssignment = (assignmentId, userInfo = null) => {
    // Find the assignment details for better context in the modal
    const assignment = assignments.find(a => a.id === assignmentId);
    setAssignmentToDelete({
      id: assignmentId,
      supervisorName: assignment?.supervisor_display_name || assignment?.supervisor_username,
      userName: assignment?.assigned_user_display_name || assignment?.assigned_user_username || userInfo?.name,
      userUsername: assignment?.assigned_user_username || userInfo?.username
    });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAssignment = async () => {
    if (!assignmentToDelete) return;

    try {
      const response = await api.delete(`/api/supervisor/assignments/${assignmentToDelete.id}`);
      
      if (response.data.success) {
        showSnackbar('Assignment removed successfully', 'success');
        fetchData(); // Refresh the data
      } else {
        showSnackbar(response.data.message || 'Failed to remove assignment', 'error');
      }
    } catch (error) {
      logger.error('Error removing assignment:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to remove assignment', 
        'error'
      );
    } finally {
      setDeleteConfirmOpen(false);
      setAssignmentToDelete(null);
    }
  };

  const cancelDeleteAssignment = () => {
    setDeleteConfirmOpen(false);
    setAssignmentToDelete(null);
  };

  // Get available users that are not already assigned to the selected supervisor
  // Sorted by domain - same domain as supervisor first, then others
  const getFilteredUsers = () => {
    if (!selectedSupervisor) return availableUsers;
    
    const assignedUserIds = assignments
      .filter(assignment => assignment.supervisor_id === parseInt(selectedSupervisor))
      .map(assignment => assignment.assigned_user_id);
    
    const filteredUsers = availableUsers.filter(user => !assignedUserIds.includes(user.id));
    
    // Get the selected supervisor's domain
    const selectedSupervisorData = supervisors.find(s => s.id === parseInt(selectedSupervisor));
    const supervisorDomain = selectedSupervisorData?.domain;
    
    // Sort users: same domain first, then others alphabetically
    return filteredUsers.sort((a, b) => {
      const aDomain = a.domain;
      const bDomain = b.domain;
      
      // If supervisor has no domain, just sort alphabetically by name
      if (!supervisorDomain) {
        const aName = a.display_name || a.username;
        const bName = b.display_name || b.username;
        return aName.localeCompare(bName);
      }
      
      // Check if users are same domain as supervisor
      const aIsSameDomain = aDomain === supervisorDomain;
      const bIsSameDomain = bDomain === supervisorDomain;
      
      // Same domain users come first
      if (aIsSameDomain && !bIsSameDomain) return -1;
      if (!aIsSameDomain && bIsSameDomain) return 1;
      
      // If both are same domain or both are different domain, sort by name
      const aName = a.display_name || a.username;
      const bName = b.display_name || b.username;
      return aName.localeCompare(bName);
    });
  };

  // Group assignments by supervisor
  const getGroupedAssignments = () => {
    const grouped = {};
    
    assignments.forEach(assignment => {
      const supervisorId = assignment.supervisor_id;
      if (!grouped[supervisorId]) {
        grouped[supervisorId] = {
          supervisor: {
            id: supervisorId,
            username: assignment.supervisor_username,
            display_name: assignment.supervisor_display_name,
            domain: assignment.supervisor_domain
          },
          users: []
        };
      }
      
      grouped[supervisorId].users.push({
        id: assignment.assigned_user_id,
        username: assignment.assigned_user_username,
        display_name: assignment.assigned_user_display_name,
        domain: assignment.assigned_user_domain,
        assigned_at: assignment.assigned_at,
        assignment_id: assignment.id
      });
    });
    
    return Object.values(grouped);
  };

  if (!isAdmin) {
    return (
      <Alert severity="error">
        Admin access required to manage supervisor assignments.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Supervisor Assignments</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {getGroupedAssignments().length} supervisors managing {assignments.length} user assignments
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={supervisors.length === 0 || availableUsers.length === 0}
        >
          Add Assignment
        </Button>
      </Box>

      {/* Summary Cards */}
      {getGroupedAssignments().length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupervisorIcon color="primary" />
              <Box>
                <Typography variant="h6">{getGroupedAssignments().length}</Typography>
                <Typography variant="caption" color="text.secondary">Active Supervisors</Typography>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" />
              <Box>
                <Typography variant="h6">{assignments.length}</Typography>
                <Typography variant="caption" color="text.secondary">Total Assignments</Typography>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupAddIcon color="success" />
              <Box>
                <Typography variant="h6">{Math.round(assignments.length / Math.max(getGroupedAssignments().length, 1))}</Typography>
                <Typography variant="caption" color="text.secondary">Avg Users/Supervisor</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Domain Legend */}
      {getGroupedAssignments().length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight="medium">Domain indicators:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip size="small" label="example.com" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              <Typography variant="caption">Organization domain</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip size="small" label="cross-domain.com" variant="outlined" color="warning" sx={{ fontSize: '0.7rem', borderStyle: 'dashed' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
              <Typography variant="caption">Cross-domain assignment</Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {assignments.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No supervisor assignments found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create assignments to allow supervisors to view statistics for specific users.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Supervisor</TableCell>
                <TableCell>Assigned Users</TableCell>
                <TableCell>Total Users</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getGroupedAssignments().map((group) => (
                <TableRow key={group.supervisor.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SupervisorIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {group.supervisor.display_name || group.supervisor.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{group.supervisor.username}
                          </Typography>
                        </Box>
                      </Box>
                      {group.supervisor.domain && (
                        <Chip 
                          label={group.supervisor.domain} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', fontWeight: 'medium' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 600 }}>
                      {group.users.map((user) => {
                        // Check if user domain differs from supervisor domain (cross-domain assignment)
                        const isCrossDomain = user.domain && group.supervisor.domain && 
                                            user.domain !== group.supervisor.domain;
                        
                        return (
                          <Box key={user.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Chip
                              icon={<PersonIcon />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{user.display_name || user.username}</span>
                                  {user.domain && (
                                    <Box 
                                      component="span" 
                                      sx={{ 
                                        fontSize: '0.65rem', 
                                        opacity: 0.7,
                                        color: isCrossDomain ? 'warning.main' : 'text.secondary'
                                      }}
                                    >
                                      @{user.domain}
                                    </Box>
                                  )}
                                </Box>
                              }
                              size="small"
                              variant="outlined"
                              color={isCrossDomain ? 'warning' : 'default'}
                              onDelete={() => handleDeleteAssignment(user.assignment_id, { 
                                name: user.display_name || user.username,
                                username: user.username 
                              })}
                              deleteIcon={
                                <Tooltip title="Remove this user assignment">
                                  <DeleteIcon />
                                </Tooltip>
                              }
                              sx={{ 
                                '& .MuiChip-deleteIcon': { 
                                  fontSize: 16,
                                  '&:hover': { 
                                    color: 'error.main' 
                                  }
                                },
                                ...(isCrossDomain && {
                                  borderStyle: 'dashed',
                                  borderWidth: 1.5
                                })
                              }}
                            />
                            {isCrossDomain && (
                              <Tooltip title="Cross-domain assignment: User belongs to a different organization">
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    bgcolor: 'warning.main',
                                    flexShrink: 0
                                  }} 
                                />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                    {group.users.length > 5 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Click on × to remove individual assignments
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={group.users.length} 
                      color="primary" 
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Add more users to this supervisor">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setSelectedSupervisor(group.supervisor.id);
                          setAssignmentMode('bulk');
                          setDialogOpen(true);
                        }}
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupAddIcon color="primary" />
            Create Supervisor Assignment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Assignment Mode Selection */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Assignment Mode</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={assignmentMode === 'single' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<PersonIcon />}
                  onClick={() => setAssignmentMode('single')}
                >
                  Single User
                </Button>
                <Button
                  variant={assignmentMode === 'bulk' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<GroupAddIcon />}
                  onClick={() => setAssignmentMode('bulk')}
                >
                  Multiple Users
                </Button>
              </Stack>
            </Box>

            <Divider />

            {/* Supervisor Selection */}
            <FormControl fullWidth>
              <InputLabel>Supervisor</InputLabel>
              <Select
                value={selectedSupervisor}
                label="Supervisor"
                onChange={(e) => setSelectedSupervisor(e.target.value)}
              >
                {supervisors.map((supervisor) => (
                  <MenuItem key={supervisor.id} value={supervisor.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SupervisorIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2">
                            {supervisor.display_name || supervisor.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{supervisor.username}
                          </Typography>
                        </Box>
                      </Box>
                      {supervisor.domain && (
                        <Chip 
                          label={supervisor.domain} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* User Selection - Single Mode */}
            {assignmentMode === 'single' && (
              <FormControl fullWidth>
                <InputLabel>User to Assign</InputLabel>
                <Select
                  value={selectedUser}
                  label="User to Assign"
                  onChange={(e) => setSelectedUser(e.target.value)}
                  disabled={!selectedSupervisor}
                >
                  {(() => {
                    const filteredUsers = getFilteredUsers();
                    const selectedSupervisorData = supervisors.find(s => s.id === parseInt(selectedSupervisor));
                    const supervisorDomain = selectedSupervisorData?.domain;
                    
                    if (!supervisorDomain) {
                      // No supervisor domain, just show all users
                      return filteredUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Box>
                                <Typography variant="body2">
                                  {user.display_name || user.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  @{user.username}
                                </Typography>
                              </Box>
                            </Box>
                            {user.domain && (
                              <Chip 
                                label={user.domain} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ));
                    }
                    
                    const sameDomainUsers = filteredUsers.filter(user => user.domain === supervisorDomain);
                    const otherDomainUsers = filteredUsers.filter(user => user.domain !== supervisorDomain);
                    
                    const result = [];
                    
                    // Same domain section
                    if (sameDomainUsers.length > 0) {
                      result.push(
                        <MenuItem key="same-domain-header" disabled sx={{ opacity: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            📍 Same Organization ({supervisorDomain})
                          </Typography>
                        </MenuItem>
                      );
                      
                      sameDomainUsers.forEach(user => {
                        result.push(
                          <MenuItem key={user.id} value={user.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Box>
                                  <Typography variant="body2">
                                    {user.display_name || user.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    @{user.username}
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip 
                                label={user.domain} 
                                size="small" 
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            </Box>
                          </MenuItem>
                        );
                      });
                    }
                    
                    // Other domains section
                    if (otherDomainUsers.length > 0) {
                      result.push(
                        <MenuItem key="other-domain-header" disabled sx={{ opacity: 1, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            🌐 Other Organizations
                          </Typography>
                        </MenuItem>
                      );
                      
                      otherDomainUsers.forEach(user => {
                        result.push(
                          <MenuItem key={user.id} value={user.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Box>
                                  <Typography variant="body2">
                                    {user.display_name || user.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    @{user.username}
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip 
                                label={user.domain} 
                                size="small" 
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            </Box>
                          </MenuItem>
                        );
                      });
                    }
                    
                    return result;
                  })()}
                </Select>
              </FormControl>
            )}

            {/* User Selection - Bulk Mode */}
            {assignmentMode === 'bulk' && (
              <FormControl fullWidth>
                <InputLabel>Users to Assign</InputLabel>
                <Select
                  multiple
                  value={selectedUsers}
                  onChange={(e) => setSelectedUsers(e.target.value)}
                  input={<OutlinedInput label="Users to Assign" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((userId) => {
                        const user = getFilteredUsers().find(u => u.id === userId);
                        return (
                          <Chip
                            key={userId}
                            label={user ? (user.display_name || user.username) : userId}
                            size="small"
                            icon={<PersonIcon />}
                          />
                        );
                      })}
                    </Box>
                  )}
                  disabled={!selectedSupervisor}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        width: 300,
                      },
                    },
                  }}
                >
                  {(() => {
                    const filteredUsers = getFilteredUsers();
                    const selectedSupervisorData = supervisors.find(s => s.id === parseInt(selectedSupervisor));
                    const supervisorDomain = selectedSupervisorData?.domain;
                    
                    if (!supervisorDomain) {
                      // No supervisor domain, just show all users
                      return filteredUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                  {user.display_name || user.username}
                                </Box>
                                {user.domain && (
                                  <Chip 
                                    label={user.domain} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: '18px', ml: 1 }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={`@${user.username}`}
                          />
                        </MenuItem>
                      ));
                    }
                    
                    const sameDomainUsers = filteredUsers.filter(user => user.domain === supervisorDomain);
                    const otherDomainUsers = filteredUsers.filter(user => user.domain !== supervisorDomain);
                    
                    const result = [];
                    
                    // Same domain section
                    if (sameDomainUsers.length > 0) {
                      result.push(
                        <MenuItem key="same-domain-header" disabled sx={{ opacity: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            📍 Same Organization ({supervisorDomain}) - {sameDomainUsers.length} users
                          </Typography>
                        </MenuItem>
                      );
                      
                      sameDomainUsers.forEach(user => {
                        result.push(
                          <MenuItem key={user.id} value={user.id}>
                            <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                    {user.display_name || user.username}
                                  </Box>
                                  <Chip 
                                    label={user.domain} 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: '18px', ml: 1 }}
                                  />
                                </Box>
                              }
                              secondary={`@${user.username}`}
                            />
                          </MenuItem>
                        );
                      });
                    }
                    
                    // Other domains section
                    if (otherDomainUsers.length > 0) {
                      result.push(
                        <MenuItem key="other-domain-header" disabled sx={{ opacity: 1, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            🌐 Other Organizations - {otherDomainUsers.length} users
                          </Typography>
                        </MenuItem>
                      );
                      
                      otherDomainUsers.forEach(user => {
                        result.push(
                          <MenuItem key={user.id} value={user.id}>
                            <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                            <ListItemText 
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                    {user.display_name || user.username}
                                  </Box>
                                  <Chip 
                                    label={user.domain} 
                                    size="small" 
                                    color="warning"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: '18px', ml: 1 }}
                                  />
                                </Box>
                              }
                              secondary={`@${user.username}`}
                            />
                          </MenuItem>
                        );
                      });
                    }
                    
                    return result;
                  })()}
                </Select>
              </FormControl>
            )}

            {/* Info Messages */}
            {selectedSupervisor && getFilteredUsers().length === 0 && (
              <Alert severity="info">
                All available users are already assigned to this supervisor.
              </Alert>
            )}

            {assignmentMode === 'bulk' && selectedUsers.length > 0 && (
              <Alert severity="success">
                {selectedUsers.length} user(s) selected for assignment
              </Alert>
            )}

            {assignmentMode === 'bulk' && getFilteredUsers().length > 10 && (
              <Alert severity="info">
                💡 Tip: Use Ctrl+Click (Cmd+Click on Mac) to select multiple users quickly
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateAssignment}
            disabled={
              !selectedSupervisor || 
              (assignmentMode === 'single' && !selectedUser) ||
              (assignmentMode === 'bulk' && selectedUsers.length === 0)
            }
            startIcon={assignmentMode === 'bulk' ? <GroupAddIcon /> : <AddIcon />}
          >
            {assignmentMode === 'bulk' 
              ? `Assign ${selectedUsers.length} User(s)` 
              : 'Create Assignment'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={cancelDeleteAssignment}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: '50%', 
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <DeleteIcon sx={{ color: 'error.main', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Remove Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          {assignmentToDelete && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to remove this supervisor assignment?
              </Typography>
              
              <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SupervisorIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {assignmentToDelete.supervisorName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Supervisor
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ px: 2 }}>
                    <Typography variant="body2" color="text.secondary">→</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {assignmentToDelete.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{assignmentToDelete.userUsername}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                <Typography variant="body2" color="warning.dark">
                  <strong>Note:</strong> The supervisor will no longer be able to view statistics and data for this user.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={cancelDeleteAssignment}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteAssignment}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ minWidth: 100 }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupervisorAssignments; 